import React, { useState } from 'react';
import { 
  CheckCircle, 
  Clock, 
  Shield, 
  Users,
  X,
  Plus,
  Trash2,
  Search,
  FlaskConical
} from 'lucide-react';

// --- مكون نافذة حجز الزيارة المنزلية ---
const HomeVisitModal = ({ isOpen, onClose }) => {
  // حالة لإدارة بيانات العميل الذي يقوم بالحجز
  const [clientInfo, setClientInfo] = useState({
    name: '',
    phone: '',
    address: ''
  });

  // حالة لإدارة بيانات المرضى. نبدأ بمريض واحد افتراضي.
  const [patients, setPatients] = useState([
    { name: '', date: '', time: '', notes: '', requiredTests: [] }
  ]);

  // حالة للتحكم في حقل البحث عن التحاليل
  const [searchQuery, setSearchQuery] = useState('');
  
  // حالة لعرض الإشعار بعد الإرسال
  const [isSubmitted, setIsSubmitted] = useState(false);

  // قائمة التحاليل المتاحة
  const allServices = [
    "تحليل صورة الدم الكاملة (CBC)",
    "تحليل وظائف الكبد (LFTs)",
    "تحليل وظائف الكلى (KFTs)",
    "تحليل السكر التراكمي (HbA1c)",
    "تحليل الغدة الدرقية (TSH, T3, T4)",
    "تحليل الفيتامينات والمعادن",
    "تحليل الدهون الكاملة (Lipid Panel)",
    "تحليل البول الكامل (Urine Analysis)",
    "تحليل الحمل (Pregnancy Test)",
    "تحليل الأنسولين (Insulin Test)",
    "تحليل الكوليسترول (LDL, HDL)",
    "تحليل المخدرات (Drug Screening)",
    "تحليل الهرمونات",
    "تحليل الفيروسات",
    "تحليل السائل المنوي",
    "تحليل الدم الخفي في البراز",
    "تحليل PSA للبروستاتا",
    "تحليل دلالات الأورام",
    "تحليل فيتامين د",
    "تحليل نسبة الحديد والفيريتين",
    "تحليل وظائف القلب (Troponin)",
    "تحليل PCR للفيروسات",
    "تحليل الحساسية"
  ];
  
  // قائمة التحاليل المفلترة بناءً على البحث
  const filteredServices = allServices.filter(service =>
    service.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  // تحديث بيانات العميل
  const handleClientChange = (event) => {
    const { name, value } = event.target;
    setClientInfo({ ...clientInfo, [name]: value });
  };

  // تحديث بيانات مريض معين
  const handlePatientChange = (index, event) => {
    const { name, value } = event.target;
    const updatedPatients = [...patients];
    updatedPatients[index] = { ...updatedPatients[index], [name]: value };
    setPatients(updatedPatients);
  };
  
  // إضافة أو إزالة تحليل من قائمة مريض معين
  const handleTestSelection = (patientIndex, service) => {
    const updatedPatients = [...patients];
    const patient = updatedPatients[patientIndex];
    
    if (patient.requiredTests.includes(service)) {
      patient.requiredTests = patient.requiredTests.filter(test => test !== service);
    } else {
      patient.requiredTests.push(service);
    }
    setPatients(updatedPatients);
  };

  // إزالة تحليل محدد من مريض معين
  const handleRemoveTest = (patientIndex, serviceToRemove) => {
    const updatedPatients = [...patients];
    updatedPatients[patientIndex].requiredTests = updatedPatients[patientIndex].requiredTests.filter(
      service => service !== serviceToRemove
    );
    setPatients(updatedPatients);
  };

  // إضافة مريض جديد
  const addPatient = () => {
    setPatients([...patients, { name: '', date: '', time: '', notes: '', requiredTests: [] }]);
  };

  // إزالة مريض
  const removePatient = (index) => {
    const updatedPatients = patients.filter((_, i) => i !== index);
    setPatients(updatedPatients);
  };

  // إرسال البيانات (في هذا المثال، يتم عرضها في الـ console)
  const handleSubmit = (e) => {
    e.preventDefault();
    console.log("تم إرسال طلب الحجز للبيانات التالية:");
    console.log("بيانات العميل:", clientInfo);
    console.log("بيانات المرضى:", patients);
    
    setIsSubmitted(true);
    // يمكنك إضافة منطق لإرسال البيانات إلى خادم هنا
    // بعد الإرسال، يمكن إعادة ضبط الحالة
    // setTimeout(() => { handleClose(); }, 3000);
  };

  // إغلاق النافذة وإعادة ضبط الحالة
  const handleClose = () => {
    onClose();
    setIsSubmitted(false);
    setClientInfo({ name: '', phone: '', address: '' });
    setPatients([{ name: '', date: '', time: '', notes: '', requiredTests: [] }]);
    setSearchQuery('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-blue-950/60 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-center justify-center font-sans p-4">
      <div className="relative bg-white rounded-2xl shadow-2xl p-6 w-full max-w-lg md:max-w-3xl max-h-[90vh] overflow-y-auto">
        {/* زر الإغلاق */}
        <button onClick={handleClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors">
          <X className="w-6 h-6" />
        </button>
        
        {isSubmitted ? (
          <div className="flex flex-col items-center justify-center p-8 text-center">
            <CheckCircle className="w-16 h-16 text-teal-500 mb-4" />
            <h3 className="text-2xl font-bold text-gray-800 mb-2">شكراً لك!</h3>
            <p className="text-gray-600">تم استلام طلبك بنجاح. سيتم التواصل معك قريباً لتأكيد الموعد.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6 text-right">
            <h3 className="text-3xl font-bold text-blue-900 mb-2">حجز زيارة منزلية</h3>
            <p className="text-gray-600 mb-6">املأ البيانات التالية لطلب زيارة منزلية من فريقنا.</p>

            {/* قسم بيانات العميل */}
            <div className="space-y-4 p-4 border rounded-xl border-gray-200">
              <h4 className="text-xl font-semibold text-gray-700">بيانات العميل</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-1">اسم العميل بالكامل</label>
                  <input
                    type="text"
                    name="name"
                    value={clientInfo.name}
                    onChange={handleClientChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                    placeholder="اسم العميل"
                    required
                  />
                </div>
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-1">رقم الهاتف</label>
                  <input
                    type="tel"
                    name="phone"
                    value={clientInfo.phone}
                    onChange={handleClientChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                    placeholder="010-1234-5678"
                    required
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-gray-700 text-sm font-medium mb-1">العنوان بالتفصيل</label>
                  <input
                    type="text"
                    name="address"
                    value={clientInfo.address}
                    onChange={handleClientChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                    placeholder="شارع 123، الحي الأول، مدينة القاهرة"
                    required
                  />
                </div>
              </div>
            </div>

            {/* قسم بيانات المرضى */}
            {patients.map((patient, index) => (
              <div key={index} className="space-y-4 p-4 border rounded-xl border-gray-200">
                <div className="flex justify-between items-center mb-2">
                  <h4 className="text-xl font-semibold text-gray-700">بيانات المريض {index + 1}</h4>
                  {patients.length > 1 && (
                    <button type="button" onClick={() => removePatient(index)} className="text-rose-500 hover:text-rose-700 transition-colors">
                      <Trash2 className="w-5 h-5" />
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-1">اسم المريض بالكامل</label>
                    <input
                      type="text"
                      name="name"
                      value={patient.name}
                      onChange={(e) => handlePatientChange(index, e)}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                      placeholder="اسم المريض"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-1">تاريخ الزيارة</label>
                    <input
                      type="date"
                      name="date"
                      value={patient.date}
                      onChange={(e) => handlePatientChange(index, e)}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-1">وقت الزيارة</label>
                    <input
                      type="time"
                      name="time"
                      value={patient.time}
                      onChange={(e) => handlePatientChange(index, e)}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                      required
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-gray-700 text-sm font-medium mb-1">ملاحظات (اختياري)</label>
                    <textarea
                      name="notes"
                      value={patient.notes}
                      onChange={(e) => handlePatientChange(index, e)}
                      rows="3"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                      placeholder="اكتب أي ملاحظات إضافية هنا..."
                    ></textarea>
                  </div>
                </div>
                
                {/* قسم التحاليل المطلوبة لهذا المريض */}
                <div className="space-y-4 p-4 border rounded-xl border-gray-200 mt-4">
                  <h4 className="text-xl font-semibold text-gray-700 flex items-center">
                    <FlaskConical className="w-5 h-5 ml-2 text-teal-600" />
                    التحاليل المطلوبة للمريض {index + 1}
                  </h4>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <input
                      type="text"
                      placeholder="ابحث عن تحليل..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="block w-full pr-10 pl-4 rounded-md border-gray-300 focus:border-teal-500 focus:ring-teal-500 text-right"
                    />
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <Search className="h-5 w-5 text-gray-400" />
                    </div>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 max-h-40 overflow-y-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-right">
                    {filteredServices.length > 0 ? (
                      filteredServices.map((service, serviceIndex) => (
                        <div key={serviceIndex} className="flex items-center space-x-2 rtl:space-x-reverse cursor-pointer">
                          <input
                            type="checkbox"
                            id={`service-${index}-${serviceIndex}`}
                            value={service}
                            checked={patient.requiredTests.includes(service)}
                            onChange={() => handleTestSelection(index, service)}
                            className="h-4 w-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                          />
                          <label htmlFor={`service-${index}-${serviceIndex}`} className="text-sm text-gray-700 cursor-pointer">
                            {service}
                          </label>
                        </div>
                      ))
                    ) : (
                      <p className="text-gray-500 text-sm text-center col-span-full">لا توجد نتائج مطابقة.</p>
                    )}
                  </div>
                  
                  {/* عرض التحاليل المختارة لهذا المريض */}
                  {patient.requiredTests.length > 0 && (
                    <div className="mt-4 p-4 bg-teal-50 rounded-lg border border-teal-200">
                      <h4 className="text-md font-bold text-teal-800 mb-2">التحاليل المحددة:</h4>
                      <div className="flex flex-wrap gap-2 text-right">
                        {patient.requiredTests.map((service, serviceIndex) => (
                          <div
                            key={serviceIndex}
                            className="bg-teal-100 text-teal-900 text-sm font-medium px-3 py-1 rounded-full border border-teal-300 flex items-center gap-1 rtl:flex-row-reverse"
                          >
                            <span>{service}</span>
                            <button
                              type="button"
                              onClick={() => handleRemoveTest(index, service)}
                              className="text-teal-500 hover:text-teal-700 p-1 rounded-full hover:bg-teal-300 transition-colors"
                              aria-label={`إزالة ${service}`}
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
            
            {/* زر إضافة مريض آخر */}
            <button
              type="button"
              onClick={addPatient}
              className="flex items-center justify-center w-full py-3 px-4 text-teal-700 bg-teal-50 hover:bg-teal-100 rounded-full transition-colors font-semibold"
            >
              <Plus className="w-5 h-5 ml-2" />
              أضف مريضًا آخر
            </button>

            <div className="flex flex-col sm:flex-row-reverse justify-start gap-4 mt-6">
              <button
                type="submit"
                className="bg-gradient-to-l from-blue-700 to-teal-600 hover:shadow-lg text-white font-semibold py-3 px-8 rounded-full shadow-md transition-all duration-300 text-lg"
              >
                إرسال الطلب
              </button>
              <button
                type="button"
                onClick={onClose}
                className="bg-gray-100 text-gray-700 font-semibold py-3 px-8 rounded-full hover:bg-gray-200 transition-colors duration-300 text-lg"
              >
                إلغاء
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

// --- مكون Hero المستقل (الآن هو المكون الرئيسي) ---
const Hero = () => {
  // حالة لإدارة ظهور نافذة حجز الزيارة المنزلية
  const [isHomeVisitModalOpen, setIsHomeVisitModalOpen] = useState(false);

  // وظيفة لفتح النافذة
  const openHomeVisitModal = () => setIsHomeVisitModalOpen(true);
  // وظيفة لإغلاق النافذة
  const closeHomeVisitModal = () => setIsHomeVisitModalOpen(false);

  return (
    <div className="bg-white text-gray-800 min-h-screen">
      <section id="home" className="relative bg-gradient-to-br from-blue-50 via-white to-teal-50 py-20 font-sans overflow-hidden">
        {/* Signature: faint pulse line ambient in the background */}
        <svg className="hidden lg:block absolute -top-6 left-0 w-full h-24 opacity-20 pointer-events-none" viewBox="0 0 400 40" preserveAspectRatio="none">
          <pattern id="heroPulse" width="100" height="40" patternUnits="userSpaceOnUse">
            <path d="M0,20 L34,20 L40,6 L46,34 L52,20 L100,20" fill="none" stroke="#0d9488" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </pattern>
          <rect width="400" height="40" fill="url(#heroPulse)" />
        </svg>

        <div className="container mx-auto px-4 relative">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Content */}
            <div className="space-y-6 text-right">
              <span className="inline-flex items-center gap-2 rounded-full bg-teal-50 text-teal-700 text-sm font-semibold px-4 py-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-teal-500"></span>
                معمل النخبه للتحاليل الطبية
              </span>
              <h1 className="text-4xl md:text-5xl font-extrabold text-blue-900 leading-tight tracking-tight">
                تحاليل طبية دقيقة
                <span className="bg-gradient-to-l from-blue-700 to-teal-600 bg-clip-text text-transparent block">وموثوقة</span>
              </h1>
              <p className="text-lg text-gray-600 leading-relaxed">
                نقدم لك أحدث التقنيات في التحاليل الطبية مع فريق من الخبراء المتخصصين 
                لضمان الحصول على نتائج دقيقة وسريعة تساعدك في الحفاظ على صحتك.
              </p>
              
              {/* Features */}
              <div className="grid grid-cols-2 gap-4 my-8">
                <div className="flex items-center gap-3">
                  <div className="p-1.5 rounded-lg bg-teal-50">
                    <CheckCircle className="w-5 h-5 text-teal-600" />
                  </div>
                  <span className="text-gray-700 font-medium">نتائج دقيقة 100%</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="p-1.5 rounded-lg bg-blue-50">
                    <Clock className="w-5 h-5 text-blue-600" />
                  </div>
                  <span className="text-gray-700 font-medium">خدمة سريعة</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="p-1.5 rounded-lg bg-purple-50">
                    <Shield className="w-5 h-5 text-purple-600" />
                  </div>
                  <span className="text-gray-700 font-medium">سرية تامة</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="p-1.5 rounded-lg bg-amber-50">
                    <Users className="w-5 h-5 text-amber-600" />
                  </div>
                  <span className="text-gray-700 font-medium">فريق متخصص</span>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={openHomeVisitModal}
                  className="bg-gradient-to-l from-blue-700 to-teal-600 hover:shadow-xl hover:-translate-y-0.5 text-white font-semibold py-3 px-8 rounded-full shadow-lg transition-all duration-300 text-lg"
                >
                  حجز الزيارة المنزلية
                </button>
                <a href="#services" className="bg-white text-center border-2 border-blue-700 text-blue-700 hover:bg-blue-700 hover:text-white font-semibold py-3 px-8 rounded-full shadow-sm transition-colors duration-300 text-lg">
                  تصفح خدماتنا
                </a>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-6 pt-8 border-t border-gray-200">
                <div className="text-center">
                  <div className="text-2xl font-extrabold text-blue-900">15+</div>
                  <div className="text-sm text-gray-500">سنة خبرة</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-extrabold text-blue-900">50K+</div>
                  <div className="text-sm text-gray-500">عميل راضي</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-extrabold text-blue-900">200+</div>
                  <div className="text-sm text-gray-500">نوع تحليل</div>
                </div>
              </div>
            </div>

            {/* Image/Visual */}
            <div className="relative">
              <div className="absolute -inset-3 rounded-[1.75rem] bg-gradient-to-br from-blue-600/15 to-teal-500/15 -z-10"></div>
              <div className="rounded-2xl overflow-hidden shadow-2xl ring-1 ring-black/5">
                <img 
                  src="https://plus.unsplash.com/premium_photo-1661411344904-65b23c4e0784?q=80&w=869&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D" 
                  alt="عالمة مختبر تعمل بالميكروسكوب" 
                  className="w-full h-96 object-cover"
                  onError={(e) => {
                    e.target.onerror = null; 
                    e.target.src = "https://placehold.co/869x960/E0E7FF/1E40AF?text=Medical+Lab";
                  }}
                />
              </div>
              
              {/* Floating Cards */}
              <div className="absolute -top-4 -right-4 bg-white rounded-2xl shadow-lg p-4 border border-gray-100 hidden lg:block">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 bg-teal-500 rounded-full animate-pulse"></div>
                  <span className="text-sm font-medium text-gray-700">متاح الآن</span>
                </div>
              </div>
              
              <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl shadow-lg p-4 border border-gray-100 hidden lg:block">
                <div className="text-center">
                  <div className="text-lg font-extrabold text-blue-900">24/7</div>
                  <div className="text-xs text-gray-500">خدمة العملاء</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* عرض نافذة الحجز إذا كانت حالتها 'true' */}
      <HomeVisitModal isOpen={isHomeVisitModalOpen} onClose={closeHomeVisitModal} />
    </div>
  );
};

export default Hero;
